import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Paper, MenuItem, TextField, Alert, CircularProgress, Stack, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../contexts/AuthContext';
import { Device, InferenceModel } from '../../components/devices/types';
import CameraUploadZipDropzone from '../../components/cameraUpload/CameraUploadZipDropzone';
import BatchProgress from '../../components/cameraUpload/BatchProgress';
import ClassificationTree from '../../components/cameraUpload/ClassificationTree';
import ImageThumbnailGrid, { PAGE_SIZE } from '../../components/cameraUpload/ImageThumbnailGrid';
import ImageCorrectionDialog from '../../components/cameraUpload/ImageCorrectionDialog';
import PushFolderToTrainingDataButton from '../../components/cameraUpload/PushFolderToTrainingDataButton';
import ExportButton from '../../components/cameraUpload/ExportButton';
import ClearImagesButton from '../../components/cameraUpload/ClearImagesButton';
import InferenceSettingsPanel from '../../components/cameraUpload/InferenceSettingsPanel';
import { CameraUploadFolder, CameraUploadImage, getCameraUploadFolders, reinferCameraUploads } from '../../api/cameraUpload';

const CAMERA_UPLOAD_CATEGORY = 'CameraUpload';

const ImageSortingPage: React.FC = () => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken ?? '';

  const [devices, setDevices] = useState<Device[]>([]);
  const [inferenceModels, setInferenceModels] = useState<InferenceModel[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const [folders, setFolders] = useState<CameraUploadFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [correctingImage, setCorrectingImage] = useState<CameraUploadImage | null>(null);
  const [reinferring, setReinferring] = useState(false);
  const [reinferError, setReinferError] = useState('');

  // Mirrors the thumbnail grid's currently-loaded page so the review dialog
  // can compute Back/Next without the grid's internal state.
  const [folderPage, setFolderPage] = useState(1);
  const [folderImages, setFolderImages] = useState<CameraUploadImage[]>([]);
  const [folderImagesTotal, setFolderImagesTotal] = useState(0);
  // Set when Back/Next crosses a page boundary, so the newly-loaded page
  // knows to select its last/first image instead of leaving nothing selected.
  const pendingEdgeSelectionRef = useRef<'first' | 'last' | null>(null);

  const loadDevices = useCallback(async (selectFirst: boolean) => {
    try {
      const res = await fetch('/api/devices', { headers: { Authorization: `Bearer ${accessToken}` } });
      if (res.ok) {
        const data: Device[] = await res.json();
        const cameraUploadDevices = data.filter((d) => d.deviceType?.deviceCategory === CAMERA_UPLOAD_CATEGORY);
        setDevices(cameraUploadDevices);
        if (selectFirst && cameraUploadDevices.length > 0) setSelectedDeviceId(cameraUploadDevices[0].id ?? '');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load devices');
    }
  }, [accessToken]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await loadDevices(true);
        const modelsRes = await fetch('/api/inference-models');
        if (modelsRes.ok) {
          setInferenceModels(await modelsRes.json());
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const selectedDevice = useMemo(
    () => devices.find((d) => d.id === selectedDeviceId) ?? null,
    [devices, selectedDeviceId],
  );

  const availableClasses = useMemo(() => {
    if (!selectedDevice?.inferenceModelId) return [];
    const model = inferenceModels.find((m) => m.id === selectedDevice.inferenceModelId);
    if (!model) return [];
    const filter = selectedDevice.inferenceClasses
      ? new Set(selectedDevice.inferenceClasses.split(',').map(Number).filter((n) => !isNaN(n)))
      : null;
    return model.classes
      .filter((c) => filter === null || filter.has(c.classIndex))
      .map((c) => c.className);
  }, [selectedDevice, inferenceModels]);

  const loadFolders = useCallback(() => {
    if (!selectedDeviceId) return;
    getCameraUploadFolders(selectedDeviceId, accessToken).then(setFolders);
  }, [selectedDeviceId, accessToken]);

  useEffect(() => {
    setSelectedFolder(null);
    setFolders([]);
    loadFolders();
  }, [selectedDeviceId, loadFolders]);

  useEffect(() => {
    setFolderPage(1);
  }, [selectedFolder]);

  useEffect(() => {
    if (pendingEdgeSelectionRef.current && folderImages.length > 0) {
      const edge = pendingEdgeSelectionRef.current;
      pendingEdgeSelectionRef.current = null;
      setCorrectingImage(edge === 'first' ? folderImages[0] : folderImages[folderImages.length - 1]);
    }
  }, [folderImages]);

  const handleUploaded = (batchId: string) => setActiveBatchId(batchId);

  const handleBatchSettled = () => {
    setActiveBatchId(null);
    loadFolders();
    setRefreshToken((t) => t + 1);
  };

  const handleCorrectionSaved = () => {
    setCorrectingImage(null);
    loadFolders();
    setRefreshToken((t) => t + 1);
  };

  // For in-dialog actions (drawing/adjusting/deleting a box) that should
  // refresh the folder tree/thumbnail grid in the background WITHOUT closing
  // the dialog — unlike handleCorrectionSaved, which is for the explicit
  // Save button and closing is the point.
  const handleBackgroundChange = () => {
    loadFolders();
    setRefreshToken((t) => t + 1);
  };

  const handleReinfer = async () => {
    setReinferring(true);
    setReinferError('');
    try {
      const { batchId } = await reinferCameraUploads(selectedDeviceId, accessToken);
      setActiveBatchId(batchId);
    } catch (err: any) {
      setReinferError(err?.message || 'Failed to start re-inference');
    } finally {
      setReinferring(false);
    }
  };

  const handleCleared = () => {
    setSelectedFolder(null);
    setFolders([]);
    setRefreshToken((t) => t + 1);
  };

  const correctingIndex = correctingImage ? folderImages.findIndex((i) => i.id === correctingImage.id) : -1;
  const totalFolderPages = Math.ceil(folderImagesTotal / PAGE_SIZE);
  const hasBack = correctingIndex > 0 || folderPage > 1;
  const hasNext = correctingIndex >= 0 && (correctingIndex < folderImages.length - 1 || folderPage < totalFolderPages);

  const handleBack = () => {
    if (correctingIndex > 0) {
      setCorrectingImage(folderImages[correctingIndex - 1]);
    } else if (folderPage > 1) {
      pendingEdgeSelectionRef.current = 'last';
      setFolderPage((p) => p - 1);
    }
  };

  const handleNext = () => {
    if (correctingIndex >= 0 && correctingIndex < folderImages.length - 1) {
      setCorrectingImage(folderImages[correctingIndex + 1]);
    } else if (folderPage < totalFolderPages) {
      pendingEdgeSelectionRef.current = 'first';
      setFolderPage((p) => p + 1);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>Image Sorting</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Upload a zip of camera photos against a Camera Upload device to have them automatically classified,
        then review and correct the results before exporting.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {devices.length === 0 ? (
        <Alert severity="info">
          No Camera Upload devices yet. Create one from the Devices page (choose the "Camera Upload" device type)
          and assign it an inference model before uploading photos here.
        </Alert>
      ) : (
        <>
          <TextField
            select
            label="Camera Upload device"
            value={selectedDeviceId}
            onChange={(e) => setSelectedDeviceId(e.target.value)}
            sx={{ mb: 3, minWidth: 300 }}
            size="small"
          >
            {devices.map((d) => (
              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
            ))}
          </TextField>

          {selectedDevice && !selectedDevice.inferenceModelId && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This device has no inference model assigned — pick one below before uploading photos.
            </Alert>
          )}

          {selectedDevice && (
            <Box sx={{ mb: 3 }}>
              <InferenceSettingsPanel
                device={selectedDevice}
                inferenceModels={inferenceModels}
                onSaved={() => loadDevices(false)}
              />
            </Box>
          )}

          {selectedDeviceId && (
            <Stack spacing={3}>
              {activeBatchId ? (
                <BatchProgress deviceId={selectedDeviceId} batchId={activeBatchId} onSettled={handleBatchSettled} />
              ) : (
                <CameraUploadZipDropzone deviceId={selectedDeviceId} onUploaded={handleUploaded} />
              )}

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
                <Paper sx={{ p: 2, minWidth: { md: 260 }, width: { xs: '100%', md: 'auto' } }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Folders</Typography>
                  <ClassificationTree folders={folders} selected={selectedFolder} onSelect={setSelectedFolder} />
                </Paper>

                <Box sx={{ flexGrow: 1, width: '100%' }}>
                  {selectedFolder ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <PushFolderToTrainingDataButton
                          deviceId={selectedDeviceId}
                          className={selectedFolder}
                          imageCount={folders.find((f) => f.className === selectedFolder)?.count ?? 0}
                        />
                      </Box>
                      <ImageThumbnailGrid
                        deviceId={selectedDeviceId}
                        className={selectedFolder}
                        refreshToken={refreshToken}
                        onImageClick={setCorrectingImage}
                        page={folderPage}
                        onPageChange={setFolderPage}
                        onImagesChange={(images, totalCount) => {
                          setFolderImages(images);
                          setFolderImagesTotal(totalCount);
                        }}
                      />
                    </>
                  ) : (
                    <Typography color="text.secondary">Select a folder to browse its photos.</Typography>
                  )}
                </Box>
              </Box>

              {reinferError && <Alert severity="error" onClose={() => setReinferError('')}>{reinferError}</Alert>}

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={reinferring ? <CircularProgress size={16} /> : <RefreshIcon />}
                  onClick={handleReinfer}
                  disabled={reinferring || folders.length === 0 || !!activeBatchId}
                >
                  Re-run inference
                </Button>
                <ExportButton deviceId={selectedDeviceId} deviceName={selectedDevice?.name ?? 'camera-upload'} disabled={folders.length === 0} />
                <ClearImagesButton
                  deviceId={selectedDeviceId}
                  deviceName={selectedDevice?.name ?? 'camera-upload'}
                  disabled={folders.length === 0}
                  onCleared={handleCleared}
                />
              </Stack>
            </Stack>
          )}
        </>
      )}

      <ImageCorrectionDialog
        deviceId={selectedDeviceId}
        image={correctingImage}
        availableClasses={availableClasses}
        onClose={() => setCorrectingImage(null)}
        onSaved={handleCorrectionSaved}
        onBackgroundChange={handleBackgroundChange}
        onBack={handleBack}
        onNext={handleNext}
        hasBack={hasBack}
        hasNext={hasNext}
      />
    </Box>
  );
};

export default ImageSortingPage;
